import { isWebView } from "./webViewBridge";

export const VITALS_BRIDGE_VERSION = 1;
export const VITALS_WEB_SOURCE = "seniorlife-web";
export const VITALS_NATIVE_SOURCE = "inochi-native";
export const REQUIRED_WELLTRA_SDK_VERSION = "5.9.3";
export const MAX_VITALS_BRIDGE_MESSAGE_CHARACTERS = 64 * 1024;

const NATIVE_TYPES = new Set([
  "NATIVE_CAPABILITIES",
  "VITALS_CAPTURE_ACCEPTED",
  "VITALS_RESULT",
  "VITALS_CANCELLED",
  "VITALS_ERROR",
]);
const REQUIRED_VITALS_METRICS = ["hr", "sbp", "dbp", "spo2", "rr"];
const VITALS_METRIC_RANGES = {
  hr: [45, 180],
  hrv_sdnn: [1, 300],
  sbp: [90, 180],
  dbp: [65, 105],
  spo2: [85, 100],
  stress: [0, 600],
  wellness: [1, 10],
  rr: [5, 60],
  hemoglobin: [5, 25],
  hba1c: [3, 20],
};

const VITALS_METRIC_LABELS = {
  hr: "Heart rate",
  hrv_sdnn: "HRV SDNN",
  sbp: "Systolic blood pressure",
  dbp: "Diastolic blood pressure",
  spo2: "Oxygen saturation",
  stress: "Stress Index",
  wellness: "Wellness Score",
  rr: "Respiratory rate",
  hemoglobin: "Hemoglobin",
  hba1c: "HbA1c",
};

const STORED_VITALS_REQUIRED_METRICS = ["hr", "sbp", "dbp", "spo2", "rr"];

export const normalizeStoredVitals = (record) => {
  const source = record?.vitals || record?.metrics || record;
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return null;
  }

  const normalized = {};
  for (const [key, range] of Object.entries(VITALS_METRIC_RANGES)) {
    if (
      source[key] === undefined ||
      source[key] === null ||
      source[key] === ""
    ) {
      continue;
    }
    const value = Number(source[key]);
    if (!Number.isFinite(value) || value < range[0] || value > range[1]) {
      if (STORED_VITALS_REQUIRED_METRICS.includes(key)) return null;
      continue;
    }
    normalized[key] = value;
  }

  if (
    !STORED_VITALS_REQUIRED_METRICS.every((key) =>
      Number.isFinite(normalized[key]),
    )
  ) {
    return null;
  }
  if (normalized.sbp <= normalized.dbp) return null;

  return normalized;
};

export const getLatestStoredVitals = (response) => {
  const records = Array.isArray(response)
    ? response
    : [
        response?.data,
        response?.items,
        response?.results,
        response?.vitals,
      ].find(Array.isArray) || [];

  return (
    records
      .map((record, index) => ({
        index,
        record,
        timestamp: Date.parse(
          record?.timestamp ||
            record?.capturedAt ||
            record?.createdAt ||
            record?.updatedAt ||
            "",
        ),
      }))
      .sort((a, b) => {
        const aTime = Number.isFinite(a.timestamp) ? a.timestamp : 0;
        const bTime = Number.isFinite(b.timestamp) ? b.timestamp : 0;
        return bTime - aTime || a.index - b.index;
      })
      .map(({ record }) => normalizeStoredVitals(record))
      .find(Boolean) || null
  );
};

const resultValidationError = (code, reason) => ({
  code,
  reason,
  recoverable: false,
});

const getVitalsMetricsValidationError = (metrics, respiratoryRate) => {
  if (!metrics || typeof metrics !== "object" || Array.isArray(metrics)) {
    return resultValidationError(
      "METRICS_MISSING",
      "No vitals measurements were returned by the SDK.",
    );
  }

  const missingMetric = REQUIRED_VITALS_METRICS.find(
    (key) => !Number.isFinite(metrics[key]),
  );
  if (missingMetric) {
    return resultValidationError(
      "REQUIRED_METRIC_MISSING",
      `${VITALS_METRIC_LABELS[missingMetric]} is missing or invalid.`,
    );
  }

  const entries = Object.entries(metrics);
  for (const [key, value] of entries) {
    const range = VITALS_METRIC_RANGES[key];
    if (!range) {
      return resultValidationError(
        "UNSUPPORTED_METRIC",
        `Unsupported metric "${key}" was returned by the SDK.`,
      );
    }
    if (!Number.isFinite(value)) {
      return resultValidationError(
        "INVALID_METRIC_VALUE",
        `${VITALS_METRIC_LABELS[key] || key} is not a valid number.`,
      );
    }
    if (value < range[0] || value > range[1]) {
      return resultValidationError(
        "METRIC_OUT_OF_RANGE",
        `${VITALS_METRIC_LABELS[key] || key} ${value} is outside the supported range ${range[0]}-${range[1]}.`,
      );
    }
  }

  if (metrics.sbp <= metrics.dbp) {
    return resultValidationError(
      "INVALID_BLOOD_PRESSURE",
      `Systolic blood pressure ${metrics.sbp} must be greater than diastolic blood pressure ${metrics.dbp}.`,
    );
  }
  if (Math.abs(metrics.rr - respiratoryRate) > 0.5) {
    return resultValidationError(
      "RESPIRATORY_RATE_MISMATCH",
      `Respiratory rate ${metrics.rr} does not match the completed measurement rate ${respiratoryRate}.`,
    );
  }

  return null;
};

export const createVitalsRequestId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `vitals-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const postVitalsWebViewMessage = ({
  type,
  bridgeSessionId,
  requestId,
  seniorId,
  payload = {},
}) => {
  if (!isWebView() || !bridgeSessionId) return false;

  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      source: VITALS_WEB_SOURCE,
      version: VITALS_BRIDGE_VERSION,
      bridgeSessionId,
      ...(requestId ? { requestId } : {}),
      ...(seniorId ? { seniorId } : {}),
      type,
      payload,
    }),
  );
  return true;
};

export const parseVitalsNativeMessage = (raw) => {
  if (
    typeof raw === "string" &&
    raw.length > MAX_VITALS_BRIDGE_MESSAGE_CHARACTERS
  ) {
    return null;
  }

  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (value.source !== VITALS_NATIVE_SOURCE) return null;
  if (value.version !== VITALS_BRIDGE_VERSION) return null;
  if (!NATIVE_TYPES.has(value.type)) return null;
  if (typeof value.bridgeSessionId !== "string" || !value.bridgeSessionId)
    return null;
  return value;
};

export const isCompatibleVitalsCapability = (message) => {
  const vitals = message?.payload?.vitals;
  return Boolean(
    message?.type === "NATIVE_CAPABILITIES" &&
      vitals?.available &&
      vitals?.sdkVersion === REQUIRED_WELLTRA_SDK_VERSION &&
      vitals?.adapterStatus === "ready",
  );
};

export const getVitalsResultValidationError = (message) => {
  const payload = message?.payload;
  const completion = payload?.completion;
  const metrics = payload?.metrics;
  const capturedAt =
    typeof payload?.capturedAt === "string"
      ? Date.parse(payload.capturedAt)
      : Number.NaN;

  if (message?.type !== "VITALS_RESULT") {
    return resultValidationError(
      "INVALID_RESULT_MESSAGE",
      "The mobile app returned an unexpected vitals message.",
    );
  }
  if (payload?.sdkVersion !== REQUIRED_WELLTRA_SDK_VERSION) {
    return resultValidationError(
      "SDK_VERSION_MISMATCH",
      `Welltra SDK ${payload?.sdkVersion || "unknown"} does not match required version ${REQUIRED_WELLTRA_SDK_VERSION}.`,
    );
  }
  if (!Number.isFinite(capturedAt)) {
    return resultValidationError(
      "INVALID_CAPTURE_TIME",
      "The vitals measurement has an invalid capture time.",
    );
  }
  if (completion?.status !== "passed") {
    return resultValidationError(
      "MEASUREMENT_NOT_PASSED",
      `The vitals measurement did not pass${completion?.status ? ` (status: ${completion.status})` : ""}.`,
    );
  }
  if (completion?.activeMeasurementSeconds !== 60) {
    return resultValidationError(
      "INVALID_MEASUREMENT_DURATION",
      `The measurement lasted ${completion?.activeMeasurementSeconds ?? "an unknown number of"} seconds; 60 seconds are required.`,
    );
  }
  const missedFaceFrames = Number(completion?.missedFaceFrames);
  if (!Number.isFinite(missedFaceFrames) || missedFaceFrames !== 0) {
    return resultValidationError(
      "FACE_TRACKING_FAILED",
      `Face tracking missed ${Number.isFinite(missedFaceFrames) ? missedFaceFrames : "an unknown number of"} frames; a complete measurement requires uninterrupted tracking.`,
    );
  }
  if (completion?.processingQueueOverflow !== false) {
    return resultValidationError(
      "PROCESSING_QUEUE_OVERFLOW",
      "The camera processing queue overflowed during the measurement.",
    );
  }
  // Image/signal quality are validated natively before a VITALS_RESULT is sent;
  // trust the native completion verdict rather than re-deriving pass/fail from
  // the raw scores against a second set of thresholds here (which would have to
  // be kept in sync with the native ones).
  if (completion?.imageQualityPassed !== true) {
    return resultValidationError(
      "IMAGE_QUALITY_FAILED",
      "The camera image did not meet the required brightness, contrast, and motion thresholds.",
    );
  }
  if (completion?.signalQualityPassed !== true) {
    return resultValidationError(
      "SIGNAL_QUALITY_FAILED",
      "The measurement signal did not meet the required quality thresholds.",
    );
  }
  if (completion?.respiratoryReady !== true) {
    return resultValidationError(
      "RESPIRATORY_NOT_READY",
      "The respiratory signal was not ready when the measurement completed.",
    );
  }

  const respiratoryRate = completion?.respiratoryRateBreathsPerMinute;
  if (!Number.isFinite(respiratoryRate)) {
    return resultValidationError(
      "RESPIRATORY_RATE_MISSING",
      "The completed measurement did not include a respiratory rate.",
    );
  }
  if (respiratoryRate < 5) {
    return resultValidationError(
      "RESPIRATORY_RATE_BELOW_THRESHOLD",
      `Respiratory rate ${respiratoryRate} is below the minimum of 5 breaths per minute.`,
    );
  }

  return getVitalsMetricsValidationError(metrics, respiratoryRate);
};

export const isCompletedVitalsResult = (message) =>
  getVitalsResultValidationError(message) === null;
